import { NodeViewWrapper } from '@tiptap/react';
import c from 'clsx';
import { flatten, map } from 'lodash';
import { forwardRef, useEffect, useState } from 'react';

import MentionsProvider from '../../services/mentions-provider';
import { sendError } from '../../utils/errors';
import Icon from '../Icon';
import { Tooltip, TooltipArrow, TooltipContent, TooltipTrigger } from '../Tooltip';

export type MentionProps = {
  id: string;
  label: string;
  onClear?: (() => void) | undefined;
  mentionsProvider: MentionsProvider;
  wrapper: typeof NodeViewWrapper | React.ElementType;
  temporal?: boolean | undefined;
};

export default forwardRef<any, MentionProps>(
  ({ mentionsProvider, id, label, onClear, wrapper: Wrapper, temporal }, _ref) => {
    const [, setProviderTick] = useState(0);
    const [tooltipOpen, setTooltipOpen] = useState(false);

    useEffect(() => {
      return mentionsProvider.onUpdate(() => {
        setProviderTick(prev => prev + 1);
      });
    }, [mentionsProvider]);

    const { suggestion, parents } = mentionsProvider.getMentionBySlug(id);
    const isTemporal = temporal ?? mentionsProvider.temporal;

    let [topParent, ...otherParents] = parents ? parents : [];
    const mentionIcon = topParent?.icon || suggestion?.icon;

    if (!suggestion) {
      topParent = {
        available: true,
        label: '',
        suggestions: [],
      };
    }

    if (suggestion?.suggestions) {
      sendError(new Error('Nested mentions are not supported'), {
        action: 'Mention#render',
        tags: {
          component: 'Mention',
          temporal: isTemporal,
        },
        params: {
          mention_id: id,
          has_parent_chain: otherParents.length > 0,
          suggestion_slug: suggestion.slug,
          suggestion_label: suggestion.label,
        },
        breadcrumb: {
          category: 'mentions',
          action: 'nested-mention-detected',
          metadata: {
            mention_id: id,
          },
        },
      });
    }

    useEffect(() => {
      if (!suggestion) {
        setTooltipOpen(false);
      }
    }, [suggestion]);

    const openTooltip = () => {
      if (suggestion) {
        setTooltipOpen(true);
      }
    };

    const closeTooltip = () => {
      setTooltipOpen(false);
    };

    const tooltipLabels = [
      ...flatten(
        [...map(otherParents, 'label')].map((label, index) => [
          <span key={otherParents[index]?.slug || `parent-${index}`} className='text-base-500'>
            {label}
          </span>,
          <Icon
            key={`chevron-${otherParents[index]?.slug || index}`}
            name='hero-chevron-right'
            className='text-base-400 h-3 w-3'
          />,
        ]),
      ),
      <span key={suggestion?.slug || 'current-suggestion'}>{suggestion ? suggestion.label : label}</span>,
    ];

    return (
      <Wrapper
        className={c('mx-[1px] inline-block', Wrapper !== NodeViewWrapper && 'mt-0.5 group-[&.condensed]:!mt-0')}
        style={{ minWidth: '0px', maxWidth: '100%' }}
        contentEditable={false}
      >
        <Tooltip placement='bottom' useArrow={true} open={tooltipOpen} onOpenChange={setTooltipOpen}>
          <TooltipTrigger asChild>
            <div
              className={c(
                // text-base-700 is pinned so the pill stays readable inside themed
                // forms, where the inherited foreground color can be white.
                'mention text-base-700 mx-0.5 mt-1 flex items-center overflow-hidden rounded-sm leading-5 ring-1 group-[&.condensed]:mx-0 group-[&.condensed]:mt-[2px]',

                {
                  'bg-indigo-100 ring-indigo-100': suggestion || isTemporal,
                  'bg-red-200 ring-red-200': !suggestion && !isTemporal,
                },
              )}
              onMouseEnter={openTooltip}
              onMouseLeave={closeTooltip}
              onFocus={openTooltip}
              onBlur={closeTooltip}
            >
              <div className='flex w-full items-center truncate'>
                {isTemporal && !suggestion && (
                  <div className='ml-1 mr-1.5 flex items-center'>
                    <Icon name='hero-at-symbol' className='text-base-400 h-3 w-3' />
                  </div>
                )}
                {!isTemporal && !suggestion && (
                  <div className='ml-1 mr-1.5 flex items-center'>
                    <Icon name='hero-exclamation-circle' className='h-3 w-3 text-red-600' />
                    {/* <Icon name='hero-at-symbol' className='h-3 w-3' /> */}
                  </div>
                )}
                {suggestion && mentionIcon && (
                  <div className='ml-1 mr-1.5 flex items-center'>
                    <Icon name={mentionIcon} className='h-3 w-3' />
                  </div>
                )}

                {/* Literal white: themed form scopes remap --color-white to the theme
                    surface, which would turn bg-white dark on dark themes. */}
                <div className='flex items-center gap-x-2 rounded-sm bg-[#fff] px-2'>
                  {suggestion ? suggestion.label : label}
                </div>
              </div>
              {onClear && (
                <div
                  className='ml-px flex cursor-pointer items-center rounded px-1 py-1 hover:bg-indigo-200'
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    onClear();
                  }}
                >
                  <Icon name='hero-x-mark' className='text-base-700 h-3 w-3 cursor-pointer' />
                </div>
              )}
            </div>
          </TooltipTrigger>

          {suggestion && (
            <TooltipContent className='transform' style={{ zIndex: 100200 }}>
              <TooltipArrow fill={'var(--color-indigo-100)'} />

              <div className='text-base-700 flex items-center rounded-sm bg-indigo-100 font-normal leading-5 shadow-md ring-2 ring-indigo-100'>
                <div className='flex w-full items-center'>
                  {topParent && topParent.icon && (
                    <div className='ml-2 mr-2 flex items-center gap-x-2'>
                      <Icon name={topParent.icon} className='h-3 w-3' />

                      <div>{topParent.label}</div>
                    </div>
                  )}

                  <div className='flex items-center gap-x-1 rounded-sm bg-[#fff] px-2'>{tooltipLabels}</div>
                </div>
              </div>
            </TooltipContent>
          )}
        </Tooltip>
      </Wrapper>
    );
  },
);
